import React from 'react';
import cn from 'classnames';
import { observer } from 'mobx-react-lite';

import { LayoutVersion } from 'widget/types/common';
import type { WidgetBodyProps } from 'widget/types/widgetShell';
import type { SendAttachment } from 'widget/types/messages';
import { useSubmissionFormStore } from 'widget/hooks/useSubmissionFormStore';
import { useToastStore } from 'widget/hooks/useToastStore';
import useChatScroll from 'widget/hooks/useChatScroll';
import ErrorToast from 'widget/shared/ui/ErrorToast';
import { AttachmentsStore } from 'widget/stores/AttachmentsStore';
import useLocal from 'widget/hooks/useLocal';
import DropZone from 'widget/components/DropZone';

import styles from './WidgetBody.module.scss';
import WidgetHeader from './WidgetHeader';
import WidgetContent from './WidgetContent';
import WidgetFooter from './WidgetFooter';

const WidgetBody: React.FC<WidgetBodyProps> = ({
  layout,
  messages,
  isTyping = false,
  hasMore = true,
  onClose,
  onSendMessage,
  onUploadAttachment,
  onLoadHistory,
  onRetry,
  className,
  customizationConfig
}) => {
  const submissionFormStore = useSubmissionFormStore();
  const toastStore = useToastStore();

  const attachmentsStore = useLocal(
    () => new AttachmentsStore(onUploadAttachment, toastStore?.showError)
  );

  React.useEffect(() => {
    attachmentsStore.setUploadFn(onUploadAttachment);
  }, [attachmentsStore, onUploadAttachment]);

  const scrollContainerRef = React.useRef<HTMLDivElement>(null);
  const displayMessages = submissionFormStore?.shouldHideContent
    ? []
    : messages;

  const { unreadCount, showScrollButton, handleScrollToBottom } = useChatScroll(
    scrollContainerRef,
    displayMessages ?? []
  );

  const handleSendMessage = React.useCallback(
    (text: string, attachments?: SendAttachment[]) => {
      if (!onSendMessage) {
        return;
      }

      onSendMessage(text, attachments);
      setTimeout(() => handleScrollToBottom(), 0);
    },
    [onSendMessage, handleScrollToBottom]
  );

  const isMobile = layout === LayoutVersion.MOBILE;

  return (
    <div
      className={cn(
        styles['widget'],
        isMobile && styles['widget_mobile'],
        className
      )}
    >
      <WidgetHeader
        onClose={onClose}
        className={styles['widget__header']}
        headerConfig={customizationConfig?.header}
      />
      <DropZone
        onDrop={attachmentsStore.addFiles}
        className={styles['widget__drop-area']}
      >
        <WidgetContent
          className={styles['widget__content']}
          messages={displayMessages}
          isTyping={isTyping}
          hasMore={hasMore}
          onLoadHistory={onLoadHistory}
          onRetry={onRetry}
          scrollContainerRef={scrollContainerRef}
          hideMessages={submissionFormStore?.shouldHideContent}
          botAvatarUrl={customizationConfig?.botAvatarUrl}
          layout={layout}
          channels={customizationConfig?.channels}
          showScrollButton={showScrollButton}
          unreadCount={unreadCount}
          onScrollToBottom={handleScrollToBottom}
        />
        {!submissionFormStore?.shouldHideContent && (
          <div className={styles['widget__footer-area']}>
            {toastStore?.message && (
              <div className={styles['widget__toast']}>
                <ErrorToast
                  key={toastStore.toastId}
                  message={toastStore.message}
                  onClose={toastStore.clear}
                />
              </div>
            )}

            <WidgetFooter
              onSendMessage={handleSendMessage}
              onBeforeSend={submissionFormStore?.onBeforeSend}
              canSendWithoutMessage={submissionFormStore?.canSendWithoutMessage}
              submissionFormDisablesSending={
                submissionFormStore?.isSendButtonDisabled
              }
              showConsents={submissionFormStore?.shouldShowConsentsInFooter}
              layout={layout}
              className={styles['widget__footer']}
              attachmentsStore={attachmentsStore}
            />
          </div>
        )}
      </DropZone>
    </div>
  );
};

export default observer(WidgetBody);
